import React, { useCallback, useMemo, useState } from "react";

import {
  Application,
  getDefaultProfileType,
  ProfileType
} from "@platformsh/observability-graph/ContinuousProfiling";
import { getUnixTime } from "date-fns";

import {
  ObservabilityPipelineContinuousProfilingFlamebearerData,
  ObservabilityPipelineContinuousProfilingTimelineData,
  ObservabilityPipelineContinuousProfilingTimelinePoint
} from "Api/observability/pipeline/types";
import { debounce } from "ds/utils/debounce";
import useQueryParams from "Hooks/useQueryStrings";
import { timeframeToRange } from "src/organization/pages/project/environment/metrics/components/Chart/settings";
import useInitialTimeframe from "src/organization/pages/project/environment/metrics/hooks/useInitialTimeframe";
import useUpdateQueryParams from "src/organization/pages/project/environment/metrics/hooks/useUpdateQueryParams";
import { HttpMetricsContext } from "src/organization/pages/project/environment/metrics/http/context";
import {
  CursorDataType,
  PageTimeFrameType
} from "src/organization/pages/project/environment/metrics/http/types";
import { defaultPollTime } from "src/organization/pages/project/environment/metrics/resources/settings";
import { parseTimeDuration } from "src/organization/pages/project/environment/metrics/utils/parseDimensions/parseDimensions";

import ComparisonTimeFrameIndicator from "./ComparisonTimeFrameIndicator";
import * as S from "./Conprof.styles";
import Controls from "./control/Controls";
import Flamebearer from "./flamebearer/Flamebearer";
import FlamebearerSkeleton from "./flamebearer/FlamebearerSkeleton";
import { ConprofDataContext } from "./hooks/useConprofContext";
import useConprofFlamebearerPoll from "./hooks/useConprofFlamebearerPoll";
import useConprofProfileTypesPoll from "./hooks/useConprofProfileTypesPoll";
import useConprofTimelinePoll from "./hooks/useConprofTimelinePoll";
import Timeline from "./timeline/Timeline";
import TimelineSkeleton from "./timeline/TimelineSkeleton";
import { ComparisonTimeFrame } from "./types";

const QueryParamComparisonStartA = "from-a";
const QueryParamComparisonEndA = "to-a";
const QueryParamComparisonStartB = "from-b";
const QueryParamComparisonEndB = "to-b";
const QueryParamProfileTypeName = "profile_type";

const Conprof = () => {
  const service = "ingester"; // hardcoded in sandbox only
  const [application, setApplication] = useState<
    Application | undefined | null
  >(undefined);
  const [timelineData, setTimelineData] = useState<
    ObservabilityPipelineContinuousProfilingTimelineData | undefined
  >(undefined);
  const [flamebearerData, setFlamebearerData] = useState<
    ObservabilityPipelineContinuousProfilingFlamebearerData | undefined
  >(undefined);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [isUpdatingProfileTypes, setUpdatingProfileTypes] = useState(true);
  const [isUpdatingTimeline, setUpdatingTimeline] = useState(true);
  const [isUpdatingFlamebearer, setUpdatingFlamebearer] = useState(true);
  const initialTimeframe = useInitialTimeframe({ id: "1h" });
  const [timeframe, setTimeFrame] =
    useState<PageTimeFrameType>(initialTimeframe);

  const {
    [QueryParamComparisonStartA]: urlComparisonStartA,
    [QueryParamComparisonEndA]: urlComparisonEndA,
    [QueryParamComparisonStartB]: urlComparisonStartB,
    [QueryParamComparisonEndB]: urlComparisonEndB,
    [QueryParamProfileTypeName]: urlProfileTypeName
  } = useQueryParams([
    QueryParamComparisonStartA,
    QueryParamComparisonEndA,
    QueryParamComparisonStartB,
    QueryParamComparisonEndB,
    QueryParamProfileTypeName
  ]);
  const [comparisonTimeframe, setComparisonTimeframe] = useState<
    ComparisonTimeFrame | undefined
  >(
    urlComparisonStartA &&
      urlComparisonEndA &&
      urlComparisonStartB &&
      urlComparisonEndB
      ? {
          sideA: {
            from: new Date(parseInt(urlComparisonStartA) * 1000),
            to: new Date(parseInt(urlComparisonEndA) * 1000)
          },
          sideB: {
            from: new Date(parseInt(urlComparisonStartB) * 1000),
            to: new Date(parseInt(urlComparisonEndB) * 1000)
          }
        }
      : undefined
  );

  const { update } = useUpdateQueryParams({});

  const [profileType, setProfileType] = useState<ProfileType | undefined>(
    undefined
  );
  const pollInterval = parseTimeDuration(
    initialTimeframe.poll || defaultPollTime[timeframe.id || "fallback"]
  );
  const [cursorData, setCursorData] =
    useState<
      CursorDataType<
        ObservabilityPipelineContinuousProfilingTimelinePoint | undefined
      >
    >();
  const debouncedSetCursorData = debounce(setCursorData);

  const handleProfileTypeChange = useCallback(
    (newProfileType: ProfileType | undefined) => {
      setTimelineData(undefined);
      setFlamebearerData(undefined);
      setProfileType(newProfileType);
      update({ [QueryParamProfileTypeName]: newProfileType?.name });
    },
    [update]
  );
  const handleComparisonModeChange = useCallback(
    (newComparisonMode: boolean) => {
      if (!newComparisonMode) {
        setComparisonTimeframe(undefined);
        update({
          [QueryParamComparisonStartA]: undefined,
          [QueryParamComparisonEndA]: undefined,
          [QueryParamComparisonStartB]: undefined,
          [QueryParamComparisonEndB]: undefined
        });
      } else {
        const { start, end } = timeframeToRange(timeframe);
        const middle = Math.floor(
          start.getTime() + (end.getTime() - start.getTime()) / 2
        );
        const comparisonTimeframe: ComparisonTimeFrame = {
          sideA: {
            from: new Date(start),
            to: new Date(middle)
          },
          sideB: {
            from: new Date(middle),
            to: new Date(end)
          }
        };
        setComparisonTimeframe(comparisonTimeframe);
        setTimeFrame({ from: start, to: end });
        update({
          [QueryParamComparisonStartA]: getUnixTime(
            comparisonTimeframe.sideA.from
          ).toString(),
          [QueryParamComparisonEndA]: getUnixTime(
            comparisonTimeframe.sideA.to
          ).toString(),
          [QueryParamComparisonStartB]: getUnixTime(
            comparisonTimeframe.sideB.from
          ).toString(),
          [QueryParamComparisonEndB]: getUnixTime(
            comparisonTimeframe.sideB.to
          ).toString()
        });
      }
    },
    [timeframe, update]
  );
  const handleTimeframeChange = useCallback(
    (newTimeframe: PageTimeFrameType) => {
      setTimelineData(undefined);
      setFlamebearerData(undefined);
      setTimeFrame(newTimeframe);
      if (newTimeframe.id) {
        setComparisonTimeframe(undefined);
        update({
          [QueryParamComparisonStartA]: undefined,
          [QueryParamComparisonEndA]: undefined,
          [QueryParamComparisonStartB]: undefined,
          [QueryParamComparisonEndB]: undefined
        });
      }
    },
    [update]
  );
  const handleComparisonTimeframeChanged = useCallback(
    (newComparisonTimeframe: ComparisonTimeFrame) => {
      setComparisonTimeframe(newComparisonTimeframe);
      update({
        [QueryParamComparisonStartA]: getUnixTime(
          newComparisonTimeframe.sideA.from
        ).toString(),
        [QueryParamComparisonEndA]: getUnixTime(
          newComparisonTimeframe.sideA.to
        ).toString(),
        [QueryParamComparisonStartB]: getUnixTime(
          newComparisonTimeframe.sideB.from
        ).toString(),
        [QueryParamComparisonEndB]: getUnixTime(
          newComparisonTimeframe.sideB.to
        ).toString()
      });
    },
    [update]
  );

  useConprofProfileTypesPoll({
    service,
    timeframe,
    interval: pollInterval.seconds,
    onChange: useCallback(
      data => {
        setApplication(data);
        setProfileType(previous => {
          if (previous || !data) {
            return previous;
          }

          if (urlProfileTypeName && data.profile_types[urlProfileTypeName]) {
            return data.profile_types[urlProfileTypeName];
          }

          const profileType = getDefaultProfileType(data);
          update({ [QueryParamProfileTypeName]: profileType?.name });
          return profileType;
        });
        setLastUpdated(new Date());
      },
      [update, urlProfileTypeName]
    ),
    onError: () => {
      setApplication(undefined);
      setProfileType(undefined);
    },
    onLoadingChange: setUpdatingProfileTypes
  });

  useConprofTimelinePoll({
    applicationName: service,
    profileTypeName: profileType?.name,
    timeframe,
    interval: pollInterval.seconds,
    onChange: useCallback(data => {
      setTimelineData(data);
      setLastUpdated(new Date());
    }, []),
    onError: () => setTimelineData(undefined),
    onLoadingChange: setUpdatingTimeline
  });

  useConprofFlamebearerPoll({
    application,
    profileTypeName: profileType?.name,
    timeframe,
    interval: pollInterval.seconds,
    onChange: useCallback(data => {
      setFlamebearerData(data);
      setLastUpdated(new Date());
    }, []),
    onError: () => setFlamebearerData(undefined),
    onLoadingChange: setUpdatingFlamebearer
  });
  const contextData: ConprofDataContext = useMemo(
    () => ({ application, profileType, timelineData, flamebearerData }),
    [application, profileType, timelineData, flamebearerData]
  );

  return (
    <HttpMetricsContext.Provider
      value={{
        cursorData,
        setCursorData: debouncedSetCursorData,
        data: contextData,
        timeframe,
        setTimeFrame
      }}
    >
      <S.Layout profileTypeName={profileType?.name}>
        <Controls
          typeframeProps={{
            isUpdating:
              isUpdatingFlamebearer ||
              isUpdatingTimeline ||
              isUpdatingProfileTypes,
            pollInterval,
            lastUpdated,
            timeframe: {
              timeframe,
              onChange: handleTimeframeChange
            }
          }}
          profileTypesProps={{
            profileTypes:
              application === null ? {} : application?.profile_types,
            profileType,
            onChange: handleProfileTypeChange
          }}
          comparisonModeProps={{
            isComparisonMode: !!comparisonTimeframe,
            onChange: handleComparisonModeChange
          }}
        />
        {comparisonTimeframe && (
          <ComparisonTimeFrameIndicator
            comparisonTimeframe={comparisonTimeframe}
          />
        )}
        {application === null || (timelineData && profileType) ? (
          <Timeline
            comparisonTimeframe={comparisonTimeframe}
            onComparisonTimeframeChanged={handleComparisonTimeframeChanged}
          />
        ) : (
          <TimelineSkeleton />
        )}
        <hr />
        {application === null || (flamebearerData && profileType) ? (
          <Flamebearer />
        ) : (
          <FlamebearerSkeleton />
        )}
      </S.Layout>
    </HttpMetricsContext.Provider>
  );
};

export default Conprof;
